import type { SurveyQuestion, InterviewQuestion } from '@bandos/shared'

export interface QuestionSourceRef {
  id: string
  label: string | null
  category: string | null
}

export interface QuestionSectionItem<Q extends SurveyQuestion | InterviewQuestion> {
  question: Q
  /** Position in the original (flat) questions array — answers are stored positionally so this matters. */
  index: number
  /**
   * Source nodes covered by this question beyond the section's primary source.
   * Empty for single-source questions; populated when one question covers several nodes
   * (e.g. a MaxDiff / comparative scale across siblings).
   */
  additionalSources: QuestionSourceRef[]
}

export interface QuestionSection<Q extends SurveyQuestion | InterviewQuestion> {
  /** Primary source node — sourceNodeIds[0] (or the legacy single sourceNodeId). */
  sourceNodeId: string | null
  sourceNodeLabel: string | null
  sourceNodeCategory: string | null
  items: QuestionSectionItem<Q>[]
}

interface NormalizedSources {
  ids: string[]
  labels: (string | null)[]
  categories: (string | null)[]
}

/**
 * Read the question's source attribution in either the new array shape or the legacy
 * single-string shape. Always returns parallel arrays (possibly empty).
 */
function normalizeSources(q: SurveyQuestion | InterviewQuestion): NormalizedSources {
  if (q.sourceNodeIds && q.sourceNodeIds.length > 0) {
    return {
      ids: q.sourceNodeIds,
      labels: q.sourceNodeLabels ?? q.sourceNodeIds.map(() => null),
      categories: q.sourceNodeCategories ?? q.sourceNodeIds.map(() => null),
    }
  }
  if (q.sourceNodeId) {
    return {
      ids: [q.sourceNodeId],
      labels: [q.sourceNodeLabel ?? null],
      categories: [q.sourceNodeCategory ?? null],
    }
  }
  return { ids: [], labels: [], categories: [] }
}

/**
 * Group a flat list of questions into sections by their primary source node.
 * Preserves the original question ordering (does NOT reorder across sections).
 * Returns a single ungrouped section (sourceNodeId: null) if no question carries
 * attribution — backwards-compatible with pre-batch surveys.
 *
 * For multi-source questions, the question is placed under its first source
 * and the remaining sources are exposed via `item.additionalSources` so renderers
 * can show "also covers X, Y" badges instead of duplicating the question.
 */
export function groupQuestionsBySource<Q extends SurveyQuestion | InterviewQuestion>(
  questions: Q[]
): QuestionSection<Q>[] {
  const normalized = questions.map(normalizeSources)
  const hasAttribution = normalized.some((s) => s.ids.length > 0)
  if (!hasAttribution) {
    return [
      {
        sourceNodeId: null,
        sourceNodeLabel: null,
        sourceNodeCategory: null,
        items: questions.map((question, index) => ({
          question,
          index,
          additionalSources: [],
        })),
      },
    ]
  }

  const sections: QuestionSection<Q>[] = []
  for (let i = 0; i < questions.length; i++) {
    const sources = normalized[i]
    const primaryId = sources.ids[0] ?? null
    const primaryLabel = sources.labels[0] ?? null
    const primaryCategory = sources.categories[0] ?? null
    const additionalSources: QuestionSourceRef[] = sources.ids.slice(1).map((id, idx) => ({
      id,
      label: sources.labels[idx + 1] ?? null,
      category: sources.categories[idx + 1] ?? null,
    }))

    const last = sections[sections.length - 1]
    if (last && last.sourceNodeId === primaryId) {
      last.items.push({ question: questions[i], index: i, additionalSources })
    } else {
      sections.push({
        sourceNodeId: primaryId,
        sourceNodeLabel: primaryLabel,
        sourceNodeCategory: primaryCategory,
        items: [{ question: questions[i], index: i, additionalSources }],
      })
    }
  }
  return sections
}

/**
 * Human-readable section heading based on the source node's category.
 */
export function sectionHeadingFor(category: string | null): string {
  if (category === 'need') return 'Problem validation'
  if (category === 'idea') return 'Solution testing'
  if (category === 'venture') return 'Market validation'
  return 'Questions'
}
